import { Box, Button, Heading, Modal, ModalBody, ModalContent, ModalOverlay, Progress, Spinner, Stack, Text, VStack, keyframes } from '@chakra-ui/react';
import { CheckIcon, CloseIcon, LockIcon } from '@chakra-ui/icons';
import { motion, AnimatePresence } from 'framer-motion';
import { accent, accentText, ink, withAlpha } from 'screens/Checkout/v2/tokens';

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.55; }
`;

const MotionBox = motion(Box);
const MotionVStack = motion(VStack);

/**
 * Étapes d'un paiement par portefeuille (Apple Pay, Google Pay).
 *
 * Le parcours quitte le site : on poste le panier à Mindbody, qui répond par
 * une redirection vers sa page de paiement hébergée, et le client revient
 * ensuite sur notre URL de retour avec son jeton. L'overlay couvre les deux
 * moments où il attend sans rien pouvoir faire, l'aller et le retour, plus
 * les deux issues.
 */
export type WalletPaymentStep =
  | 'idle'
  /** Aller : le panier part chez Mindbody, la redirection est imminente. */
  | 'redirecting'
  /** Retour : on finalise la commande avec le jeton rapporté. */
  | 'finalizing'
  /** La vente est créée, les crédits arrivent sur le compte. */
  | 'crediting'
  | 'success'
  /** Mindbody n'a pas encore tranché : ni refusé, ni abouti. */
  | 'pending'
  | 'failed';

interface StepConfig {
  title: string;
  description: string;
  showProgress: boolean;
}

const STEP_CONFIGS: Record<WalletPaymentStep, StepConfig> = {
  idle: { title: '', description: '', showProgress: false },
  redirecting: {
    title: 'Ouverture du paiement sécurisé',
    description: 'Nous vous emmenons sur la page de paiement, où vous validerez avec votre portefeuille.',
    showProgress: true,
  },
  finalizing: {
    title: 'Validation de votre paiement',
    description: 'Nous confirmons la transaction, cela prend quelques secondes.',
    showProgress: true,
  },
  crediting: {
    title: 'Ajout de vos crédits',
    description: 'Votre paiement est accepté, nous créditons votre compte.',
    showProgress: true,
  },
  success: {
    title: "C'est terminé !",
    description: 'Redirection vers vos crédits…',
    showProgress: false,
  },
  pending: {
    title: 'Paiement en cours de confirmation',
    description:
      "Votre banque n'a pas encore rendu sa réponse. Dès qu'elle arrive, vos crédits sont ajoutés automatiquement : ne payez pas une seconde fois.",
    showProgress: false,
  },
  failed: {
    title: 'Paiement non confirmé',
    // On se garde d'affirmer qu'aucun montant n'a été prélevé : cet écran
    // s'affiche aussi quand le paiement a pu aboutir sans que la commande
    // puisse être finalisée (réseau coupé au retour, session perdue). La
    // reprise automatique rattrape ces cas, mais le client doit lire quelque
    // chose de vrai en attendant.
    description: "Nous n'avons pas pu confirmer votre paiement. Aucun crédit n'a encore été ajouté à votre compte.",
    showProgress: false,
  },
};

export interface WalletPaymentOverlayProps {
  isOpen: boolean;
  currentStep: WalletPaymentStep;
  /** Nom du portefeuille, pour parler au client comme il a choisi de payer. */
  walletName?: string;
  /** Détail technique éventuel renvoyé par le serveur. */
  errorMessage?: string | null;
  /** Retour au panier, avec le moyen de paiement à rechoisir. */
  onRetry?: () => void;
  /** Sortie de secours vers le compte. */
  onGoToAccount?: () => void;
}

/**
 * Overlay plein écran du paiement par portefeuille. Même langage que
 * AlmaPaymentOverlay et ContractPaymentOverlay (fenêtre centrée, fond flouté,
 * étapes nommées, « ne fermez pas la page »), avec deux différences voulues :
 * l'attente porte la couleur du club plutôt qu'un orange fixe, et l'échec
 * propose une issue au lieu de laisser le client devant un message.
 */
export function WalletPaymentOverlay({ isOpen, currentStep, walletName, errorMessage, onRetry, onGoToAccount }: WalletPaymentOverlayProps) {
  const config = STEP_CONFIGS[currentStep];
  const isWaiting = currentStep === 'redirecting' || currentStep === 'finalizing' || currentStep === 'crediting';
  const isSettled = currentStep === 'failed' || currentStep === 'pending';

  const description =
    currentStep === 'redirecting' && walletName
      ? `Nous vous emmenons sur la page de paiement, où vous validerez avec ${walletName}.`
      : config.description;

  const renderIcon = () => {
    if (currentStep === 'success') {
      return (
        <MotionBox initial={{ scale: 0, rotate: -180 }} animate={{ scale: 1, rotate: 0 }} transition={{ type: 'spring', stiffness: 200, damping: 15 }}>
          <Box w={16} h={16} borderRadius="full" bg="green.100" display="flex" alignItems="center" justifyContent="center">
            <CheckIcon w={8} h={8} color="green.600" />
          </Box>
        </MotionBox>
      );
    }
    if (currentStep === 'failed') {
      return (
        <MotionBox initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: 'spring', stiffness: 200, damping: 15 }}>
          <Box w={16} h={16} borderRadius="full" bg="red.50" display="flex" alignItems="center" justifyContent="center">
            <CloseIcon w={6} h={6} color="red.600" />
          </Box>
        </MotionBox>
      );
    }
    return (
      <Box
        w={16}
        h={16}
        borderRadius="full"
        bg={withAlpha(accent, 0.12)}
        display="flex"
        alignItems="center"
        justifyContent="center"
        animation={`${pulse} 2s ease-in-out infinite`}
      >
        <Spinner size="lg" color={accent} thickness="3px" speed="0.8s" emptyColor={withAlpha(accent, 0.18)} />
      </Box>
    );
  };

  return (
    <Modal isOpen={isOpen} onClose={() => {}} isCentered closeOnOverlayClick={false} closeOnEsc={false} size="md">
      <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(10px)" />
      <ModalContent bg="white" border="1px solid" borderColor="gray.200" borderRadius="2xl" boxShadow="2xl" mx={4}>
        <ModalBody p={8}>
          <AnimatePresence mode="wait">
            <MotionVStack
              key={currentStep}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
              spacing={6}
            >
              <VStack spacing={4}>
                {renderIcon()}
                <VStack spacing={2} textAlign="center">
                  <Heading size="lg" color={ink}>
                    {config.title}
                  </Heading>
                  <Text color="gray.600" fontSize="md">
                    {description}
                  </Text>
                </VStack>
              </VStack>

              {config.showProgress && (
                <Box w="full">
                  <Progress isIndeterminate bg="gray.100" borderRadius="full" size="sm" sx={{ '& > div': { backgroundColor: accent } }} />
                </Box>
              )}

              {isWaiting && (
                <VStack spacing={3}>
                  <Text fontSize="sm" color="gray.500" textAlign="center" lineHeight="1.5">
                    Merci de <strong>ne pas fermer ni rafraîchir cette page</strong> pendant le traitement.
                  </Text>
                  <Box display="flex" alignItems="center" gap={2} color="gray.500">
                    <LockIcon w={3} h={3} />
                    <Text fontSize="xs">Paiement sécurisé par Mindbody Payments</Text>
                  </Box>
                </VStack>
              )}

              {isSettled && (
                <VStack spacing={4} w="full">
                  <Box bg="gray.50" p={4} borderRadius="lg" border="1px solid" borderColor="gray.200" w="full">
                    <Text fontSize="sm" color="gray.700" lineHeight="1.6">
                      {currentStep === 'pending'
                        ? "Vous pouvez fermer cette page : la commande se termine de notre côté. Vos crédits apparaîtront dans votre compte, et nous vous écrivons si quelque chose bloque."
                        : "Si vous avez été débité(e), votre commande sera honorée : nous la rattrapons automatiquement dans les minutes qui suivent. Sans nouvelle de notre part, écrivez-nous et nous régularisons."}
                    </Text>
                    {errorMessage && (
                      <Text fontSize="xs" color="gray.500" mt={2}>
                        {errorMessage}
                      </Text>
                    )}
                  </Box>
                  <Stack direction={{ base: 'column', sm: 'row' }} spacing={3} w="full">
                    {onRetry && (
                      <Button flex="1" bg={accent} color={accentText} _hover={{ opacity: 0.9 }} onClick={onRetry}>
                        Choisir un autre moyen
                      </Button>
                    )}
                    {onGoToAccount && (
                      <Button flex="1" variant="outline" borderColor="gray.300" color="gray.700" onClick={onGoToAccount}>
                        Voir mon compte
                      </Button>
                    )}
                  </Stack>
                </VStack>
              )}
            </MotionVStack>
          </AnimatePresence>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
